// Copyright 2026 Mo-Sys Engineering Ltd. All Rights Reserved.
import { createAsyncThunk } from '@reduxjs/toolkit';

import axiosInstance from '../../services/axiosInstance';
import { apiCall } from '../utils/utils';

import { uploadImageAndRefresh } from '../utils/imageCache';

import { selectStudioUUIDByEquipmentUUID } from './equipmentSelectors';

import {
  wsStudioEquipmentDesiredState,
  wsStudioEquipmentAction,
} from '../../../web-utils/src/store/Websocket/WebsocketActions';

import { clearNodePosition } from '../userPreferences/userPreferencesSlice';

// get block equipment details
export const fetchBlockEquipmentDetails = createAsyncThunk(
  'equipment/fetchBlockEquipmentDetails',
  async (blockEquipId, { rejectWithValue }) =>
    apiCall(() => axiosInstance.get(`block_equipment/detail/${blockEquipId}/`), rejectWithValue)
);

// get a company equipment details by its uuid
export const fetchEquipmentDetails = createAsyncThunk(
  'equipment/fetchEquipmentDetails',
  async ({ companyUuid, equipUUID }, { rejectWithValue }) =>
    apiCall(
      () => axiosInstance.get(`company_equipment/${companyUuid}/detail/${equipUUID}/`),
      rejectWithValue
    )
);

// fetch all equipment groups in a Company
export const fetchCompanyEquipmentGroups = createAsyncThunk(
  'equipment/fetchCompanyEquipmentGroups',
  async (companyId, { rejectWithValue }) =>
    apiCall(() => axiosInstance.get(`equipment-group/company/${companyId}/`), rejectWithValue)
);

// fetch an equipment group details by its uuid
export const fetchEquipmentGroupById = createAsyncThunk(
  'equipment/fetchEquipmentGroupById',
  async (equipGroupUUID, { rejectWithValue }) =>
    apiCall(() => axiosInstance.get(`equipment-group/${equipGroupUUID}/`), rejectWithValue)
);

export const fetchControlGroupById = createAsyncThunk(
  'equipment/fetchControlGroupById',
  async (equipGroupUUID, { rejectWithValue }) =>
    apiCall(() => axiosInstance.get(`equipment-group/${equipGroupUUID}/`), rejectWithValue)
);

// create an equipment group
export const createEquipmentGroup = createAsyncThunk(
  'equipment/createEquipmentGroup',
  async (data, { rejectWithValue }) =>
    apiCall(() => axiosInstance.post(`equipment-group/`, data), rejectWithValue)
);

// update an equipment group
export const updateEquipmentGroup = createAsyncThunk(
  'equipment/updateEquipmentGroup',
  async (data, { rejectWithValue }) =>
    apiCall(() => axiosInstance.patch(`equipment-group/`, data), rejectWithValue)
);

// delete an equipment group
export const deleteEquipmentGroup = createAsyncThunk(
  'equipment/deleteEquipmentGroup',
  async ({ equipGroupUUID, cascade = true }, { rejectWithValue }) =>
    apiCall(
      () => axiosInstance.delete(`equipment-group/${equipGroupUUID}/${cascade}/`),
      rejectWithValue
    )
);

// delete an equipment group and children
export const deleteEquipmentGroupAndChildren = createAsyncThunk(
  'equipment/deleteEquipmentGroupAndChildren',
  async (equipGroupUUID, { rejectWithValue }) =>
    apiCall(() => axiosInstance.delete(`equipment-group/${equipGroupUUID}/true/`), rejectWithValue)
);

// update equipment group item
export const updateEquipmentGroupItem = createAsyncThunk(
  'equipment/updateEquipmentGroupItem',
  async (data, { rejectWithValue }) =>
    apiCall(() => axiosInstance.patch(`equipment-group/items/`, data), rejectWithValue)
);

// update equipment group items (equipments and sub-groups)
export const updateEquipmentGroupItems = createAsyncThunk(
  'equipment/updateEquipmentGroupItems',
  async ({ data, checkEquipmentUsed = false }, { getState, dispatch, rejectWithValue }) => {
    try {
      const state = getState();
      const { equip_group_uuid: equipGroupUUID, add_equipment: addEquipment = [] } = data;

      // Throw an error if equipGroupUUID is not provided
      if (!equipGroupUUID) {
        return rejectWithValue('Missing equipment group UUID');
      }
      // Check if any of the equipment being added is already part of another group to prevent duplicates in the React Flow
      if (addEquipment.length && checkEquipmentUsed) {
        const allUsedEquipment =
          state.equipment?.equipmentGroups?.flatMap(group => group.equipment || []) || [];

        const conflictingEquipment = allUsedEquipment.filter(equip =>
          addEquipment.includes(equip.co_equip_uuid)
        );

        if (conflictingEquipment?.length) {
          const names = conflictingEquipment.map(
            equip => equip.equip_name || equip.name || 'Unknown Equipment'
          );

          return rejectWithValue(`Equipment already in use: ${names.join(', ')}`);
        }
      }

      return dispatch(updateEquipmentGroupItem(data)).unwrap();
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Handle drag/drop equipment or equipment group node
export const moveNodeBetweenGroups = createAsyncThunk(
  'equipment/moveNodeBetweenGroups',
  async (
    { nodeId, fromGroupId, toGroupId, type = 'equipment', checkEquipmentUsed = false },
    { dispatch, rejectWithValue }
  ) => {
    if (!nodeId || !toGroupId) {
      return rejectWithValue('Missing node or destination group');
    }
    // prevent self-move (group → itself)
    if (type === 'equipmentGroup' && nodeId === toGroupId) {
      return rejectWithValue('Cannot move group into itself');
    }

    //  prevent same group move
    if (fromGroupId && fromGroupId === toGroupId) {
      return rejectWithValue('Cannot move within same group');
    }

    const typeMap = {
      equipment: 'equipment',
      equipmentGroup: 'equipment_group',
    };

    const entityKey = typeMap[type];

    //  If moving from a group → remove first
    if (fromGroupId) {
      const data = {
        equip_group_uuid: fromGroupId,
        [`remove_${entityKey}`]: [nodeId],
      };

      const removeResult = await dispatch(updateEquipmentGroupItems({ data, checkEquipmentUsed }));

      if (updateEquipmentGroupItems.rejected.match(removeResult)) {
        return rejectWithValue(removeResult?.payload);
      } else {
        dispatch(clearNodePosition(nodeId));
      }
    }

    //  Add to destination group
    const data = {
      equip_group_uuid: toGroupId,
      [`add_${entityKey}`]: [nodeId],
    };

    const addResult = await dispatch(updateEquipmentGroupItems({ data }));

    if (updateEquipmentGroupItems.rejected.match(addResult)) {
      return rejectWithValue(addResult?.payload);
    }

    return addResult.payload;
  }
);

// Helper for add / remove connection
const connectionBody = (connection, nodes) => {
  const { source, target, sourceHandle, targetHandle } = connection;

  const sourceNode = nodes.find(n => n.id === source);
  const targetNode = nodes.find(n => n.id === target);

  if (!sourceNode || !targetNode) {
    return { body: null, error: 'Source or target not found' };
  }

  // Prevent self-connection
  if (source === target) {
    return { body: null, error: 'Source and target cannot be the same' };
  }

  return {
    body: {
      source: {
        uuid: sourceNode.id,
        node_type: sourceNode.type === 'equipment' ? 'equipment' : 'group',
        handle: sourceHandle,
      },
      target: {
        uuid: targetNode.id,
        node_type: targetNode.type === 'equipment' ? 'equipment' : 'group',
        handle: targetHandle,
      },
    },
    error: null,
  };
};

// Remove connection between two equipment
export const removeConnection = createAsyncThunk(
  'equipment/removeConnection',
  async (
    { connection, nodes, facilityEquipmentGroupUuid },
    { getState, rejectWithValue, dispatch }
  ) => {
    const { body, error } = connectionBody(connection, nodes);
    if (!body) {
      return rejectWithValue(error);
    }

    return await apiCall(
      () => axiosInstance.post('connections/disconnect/', body),
      rejectWithValue
    );
  }
);

// Handle adding a connection between two nodes (equipment or equipment group) in the React Flow and send appropriate websocket messages to update equipment settings based on the connection handles and protocols
export const addConnection = createAsyncThunk(
  'equipment/addConnection',
  async (
    { connection, nodes, facilityEquipmentGroupUuid },
    { getState, rejectWithValue, dispatch }
  ) => {
    const { body, error } = connectionBody(connection, nodes);
    if (!body) {
      return rejectWithValue(error);
    }

    return await apiCall(() => axiosInstance.post('connections/connect/', body), rejectWithValue);
  }
);

/*
---------------------------------------------------------
Equipment motion controller store
---------------------------------------------------------
One controller per equipment device
This replaces throttleMap, moveTimeoutMap, lastPayloadMap
*/
const equipmentControllers = {};

/*
---------------------------------------------------------
Motion constants
---------------------------------------------------------
*/

/*
---------------------------------------------------------
controlEquipment
Handles continuous updates + debounced move command
---------------------------------------------------------
*/

export const controlEquipment = createAsyncThunk(
  'equipment/controlEquipment',
  async (
    { equipmentUUID, settings, action, timestamp },
    { getState, dispatch, rejectWithValue }
  ) => {
    const state = getState();
    const studioUUID = selectStudioUUIDByEquipmentUUID(state, equipmentUUID);

    if (!studioUUID || !equipmentUUID) {
      return rejectWithValue('Missing studioUUID or equipmentUUID');
    }

    if (!equipmentControllers[equipmentUUID]) {
      equipmentControllers[equipmentUUID] = {
        activeSession: null,
        lastSettings: null,
        isMoving: false,
      };
    }

    const controller = equipmentControllers[equipmentUUID];

    // Send settings ONLY if changed
    const settingsChanged = JSON.stringify(settings) !== JSON.stringify(controller.lastSettings);
    if (settings && settingsChanged) {
      controller.lastSettings = settings;
      await dispatch(
        wsStudioEquipmentDesiredState({
          studioUUID,
          equipmentUUID,
          settings,
        })
      );
    }
    if (!action) {
      return { equipmentUUID };
    }
    if (action !== 'move' && action !== 'nudge') {
      await dispatch(
        wsStudioEquipmentAction({
          studioUUID,
          equipmentUUID,
          action,
        })
      );

      return { equipmentUUID };
    }

    // Send MOVE if nudging, if settings change or only once per move drag
    if (action === 'nudge' || settingsChanged || (action === 'move' && !controller.isMoving)) {
      if (action === 'move') {
        controller.isMoving = true;
      }

      await dispatch(
        wsStudioEquipmentAction({
          studioUUID,
          equipmentUUID,
          action: 'move',
        })
      );
    }

    return { equipmentUUID };
  }
);

/*
---------------------------------------------------------
controlEquipmentAction
Handles actions like STOP
---------------------------------------------------------
*/

export const controlEquipmentAction = createAsyncThunk(
  'equipment/controlEquipmentAction',
  async ({ equipmentUUID, action }, { getState, dispatch, rejectWithValue }) => {
    const state = getState();
    const studioUUID = selectStudioUUIDByEquipmentUUID(state, equipmentUUID);

    if (!studioUUID || !equipmentUUID) {
      return rejectWithValue('Missing studioUUID or equipmentUUID');
    }

    const controller = equipmentControllers[equipmentUUID];

    await dispatch(
      wsStudioEquipmentAction({
        studioUUID,
        equipmentUUID,
        action,
      })
    );

    if (controller) {
      controller.activeSession = null;
      controller.isMoving = false;
      controller.lastSettings = null;
    }

    return { equipmentUUID, action };
  }
);

// update a shot console's button assignments
export const updateButtonAssignments = createAsyncThunk(
  'equipment/updateButtonAssignments',
  async ({ shotConsoleUUID, payload }, { getState, rejectWithValue, dispatch }) => {
    if (!shotConsoleUUID) {
      const message = `Shot Console invalid: ${shotConsoleUUID}`;
      return rejectWithValue(message);
    }
    const studioUUID = selectStudioUUIDByEquipmentUUID(getState(), shotConsoleUUID);
    if (!studioUUID) {
      const message = `Studio not found for Shot Console ${shotConsoleUUID}`;
      return rejectWithValue(message);
    }
    dispatch(
      wsStudioEquipmentDesiredState({
        studioUUID,
        equipmentUUID: shotConsoleUUID,
        settings: {
          button_config: payload,
        },
      })
    );
  }
);

// ---------------------------------------------------
// Equipment Group Lock
// ---------------------------------------------------

// Lock an equipment group
export const lockEquipmentGroup = createAsyncThunk(
  'equipment/lockEquipmentGroup',
  async (data, { rejectWithValue }) =>
    apiCall(() => axiosInstance.post(`equipment-group/lock/`, data), rejectWithValue)
);

// accept Lock request for an equipment group
export const acceptLockRequest = createAsyncThunk(
  'equipment/acceptLockRequest',
  async (data, { rejectWithValue }) =>
    apiCall(() => axiosInstance.post(`equipment-group/lock_request/accept/`, data), rejectWithValue)
);

// reject / cancel Lock request for an equipment group
export const rejectLockRequest = createAsyncThunk(
  'equipment/rejectLockRequest',
  async (data, { rejectWithValue }) =>
    apiCall(() => axiosInstance.post(`equipment-group/lock_request/reject/`, data), rejectWithValue)
);

// Unlock an equipment group
export const unlockEquipmentGroup = createAsyncThunk(
  'equipment/unlockEquipmentGroup',
  async (data, { rejectWithValue }) =>
    apiCall(() => axiosInstance.post(`equipment-group/unlock/`, data), rejectWithValue)
);

// Unlock all user's locks
export const unlockAllEquipmentGroups = createAsyncThunk(
  'equipment/unlockAllEquipmentGroups',
  async (data, { rejectWithValue }) =>
    apiCall(() => axiosInstance.post(`equipment-group/unlock/user/`, data), rejectWithValue)
);

// Admin only: unlock all locks (and optionally discard pending lock requests) within an equipment group
export const unlockAllEquipmentGroupLocks = createAsyncThunk(
  'equipment/unlockAllEquipmentGroupLocks',
  async (data, { rejectWithValue }) =>
    apiCall(() => axiosInstance.post(`equipment-group/unlock/all/`, data), rejectWithValue)
);

// ---------------------------------------------------
// Equipment Group Types
// ---------------------------------------------------

// fetch all equipment group types
export const fetchEquipmentGroupTypes = createAsyncThunk(
  'equipment/fetchEquipmentGroupTypes',
  async (_, { rejectWithValue }) =>
    apiCall(() => axiosInstance.get(`equipment-group/type/`), rejectWithValue)
);

// Create a equipment group type
export const createEquipmentGroupType = createAsyncThunk(
  'equipment/createEquipmentGroupType',
  async (data, { rejectWithValue }) =>
    apiCall(() => axiosInstance.post(`equipment-group/type/`, data), rejectWithValue)
);

// Update a equipment group type
export const updateEquipmentGroupType = createAsyncThunk(
  'equipment/updateEquipmentGroupType',
  async (data, { rejectWithValue }) =>
    apiCall(() => axiosInstance.patch(`equipment-group/type/`, data), rejectWithValue)
);

// Delete a equipment group type
export const deleteEquipmentGroupType = createAsyncThunk(
  'equipment/deleteEquipmentGroupType',
  async (equipTypeId, { rejectWithValue }) =>
    apiCall(() => axiosInstance.delete(`equipment-group/type/${equipTypeId}/`), rejectWithValue)
);

// ---------------------------------------------------
// Equipment Group Type Rule
// ---------------------------------------------------

// get equipment group type rule
export const fetchEquipmentGroupTypeRule = createAsyncThunk(
  'equipment/fetchEquipmentGroupTypeRule',
  async (equipTypeRuleId, { rejectWithValue }) =>
    apiCall(
      () => axiosInstance.get(`equipment-group/type/rule/${equipTypeRuleId}/`),
      rejectWithValue
    )
);

// Create a equipment group type rule
export const createEquipmentGroupTypeRule = createAsyncThunk(
  'equipment/createEquipmentGroupTypeRule',
  async (data, { rejectWithValue }) =>
    apiCall(() => axiosInstance.post(`equipment-group/type/rule/`, data), rejectWithValue)
);

// Update a equipment group type rule
export const updateEquipmentGroupTypeRule = createAsyncThunk(
  'equipment/updateEquipmentGroupTypeRule',
  async (data, { rejectWithValue }) =>
    apiCall(() => axiosInstance.patch(`equipment-group/type/rule/`, data), rejectWithValue)
);

// Delete a equipment group type rule
export const deleteEquipmentGroupTypeRule = createAsyncThunk(
  'equipment/deleteEquipmentGroupTypeRule',
  async (equipTypeRuleId, { rejectWithValue }) =>
    apiCall(
      () => axiosInstance.delete(`equipment-group/type/rule/${equipTypeRuleId}/`),
      rejectWithValue
    )
);

// ---------------------------------------------------
// Equipment Group Type Rule Set
// ---------------------------------------------------

// Get all equipment Group type rule sets
export const fetchEquipmentGroupTypeRuleSets = createAsyncThunk(
  'equipment/fetchEquipmentGroupTypeRuleSets',
  async (_, { rejectWithValue }) =>
    apiCall(() => axiosInstance.get(`equipment-group/type/rule/set/`), rejectWithValue)
);

// Create a equipment group type rule set
export const createEquipmentGroupTypeRuleSet = createAsyncThunk(
  'equipment/createEquipmentGroupTypeRuleSet',
  async (data, { rejectWithValue }) =>
    apiCall(() => axiosInstance.post(`equipment-group/type/rule/set/`, data), rejectWithValue)
);

// Update a equipment group type rule set
export const updateEquipmentGroupTypeRuleSet = createAsyncThunk(
  'equipment/updateEquipmentGroupTypeRuleSet',
  async (data, { rejectWithValue }) =>
    apiCall(() => axiosInstance.patch(`equipment-group/type/rule/set/`, data), rejectWithValue)
);

// Delete a equipment group type rule set
export const deleteEquipmentGroupTypeRuleSet = createAsyncThunk(
  'equipment/deleteEquipmentGroupTypeRuleSet',
  async (equipTypeRuleSetId, { rejectWithValue }) =>
    apiCall(
      () => axiosInstance.delete(`equipment-group/type/rule/set/${equipTypeRuleSetId}/`),
      rejectWithValue
    )
);

// ---------------------------------------------------
// Preset
// ---------------------------------------------------

// fetch all Presets related to an Equipment Group's family tree
export const fetchPresetsForFamilyTree = createAsyncThunk(
  'equipment/fetchPresetsForFamilyTree',
  async (equipGroupUUID, { rejectWithValue }) =>
    apiCall(() => axiosInstance.get(`equipment-group/${equipGroupUUID}/presets/`), rejectWithValue)
);

// get all presets for a equipment group
// Not used atm replaced by fetchPresetsForFamilyTree
// export const fetchPresets = createAsyncThunk(
//   'equipment/fetchPresets',
//   async (equipGroupUUID, { rejectWithValue }) =>
//     apiCall(() => axiosInstance.get(`preset/equip-group/${equipGroupUUID}/`), rejectWithValue)
// );

// create a preset for an equipment group
export const createPreset = createAsyncThunk(
  'equipment/createPreset',
  async (data, { rejectWithValue }) =>
    apiCall(() => axiosInstance.post(`preset/`, data), rejectWithValue)
);

// update a preset
export const updatePreset = createAsyncThunk(
  'equipment/updatePreset',
  async (data, { rejectWithValue }) =>
    apiCall(() => axiosInstance.patch(`preset/`, data), rejectWithValue)
);

// delete a preset
export const deletePreset = createAsyncThunk(
  'equipment/deletePreset',
  async (presetId, { rejectWithValue }) =>
    apiCall(() => axiosInstance.delete(`preset/${presetId}/`), rejectWithValue)
);

// apply preset
export const applyPreset = createAsyncThunk(
  'equipment/applyPreset',
  async (data, { rejectWithValue }) =>
    apiCall(() => axiosInstance.post(`preset/apply/`, data), rejectWithValue)
);

// stop a preset
export const stopPreset = createAsyncThunk(
  'equipment/stopPreset',
  async (data, { rejectWithValue }) =>
    apiCall(() => axiosInstance.post(`preset/stop/`, data), rejectWithValue)
);

// stop all preset movements in a studio
export const stopAllPresetsInStudio = createAsyncThunk(
  'equipment/stopAllPresetsInStudio',
  async (data, { rejectWithValue }) =>
    apiCall(() => axiosInstance.post(`preset/stop-all/`, data), rejectWithValue)
);

// update preset image
export const updatePresetImage = createAsyncThunk(
  'equipment/updatePresetImage',
  async (
    { presetId, imgType = 'Large', lastUpdated = '', defaultImage = '' },
    { rejectWithValue }
  ) => {
    try {
      const result = await uploadImageAndRefresh({
        id: presetId,
        sourceId: null,
        imgType,
        endpointPrefix: 'preset',
        sourceEndpointPrefix: 'preset', // null?
        lastUpdated,
        defaultImage,
      });
      return result;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.detail?.[0]?.msg || error?.message || 'Failed to update block image'
      );
    }
  }
);

// ---------------------------------------------------
// Preset Sequence
// ---------------------------------------------------

// get all preset sequences for a EquipmentGroup
// Not used atm replaced by fetchPresetsForFamilyTree
// export const fetchPresetSequencesForGroup = createAsyncThunk(
//   'equipment/fetchPresetSequencesForGroup',
//   async (equipGroupUUID, { rejectWithValue }) =>
//     apiCall(
//       () => axiosInstance.get(`preset/sequence/equip-group/${equipGroupUUID}/`),
//       rejectWithValue
//     )
// );

// get a preset sequence by id
export const fetchPresetSequenceById = createAsyncThunk(
  'equipment/fetchPresetSequenceById',
  async (presetSequenceId, { rejectWithValue }) =>
    apiCall(() => axiosInstance.get(`preset/sequence/${presetSequenceId}/`), rejectWithValue)
);

// create a preset sequence
export const createPresetSequence = createAsyncThunk(
  'equipment/createPresetSequence',
  async (data, { rejectWithValue }) =>
    apiCall(() => axiosInstance.post(`preset/sequence/`, data), rejectWithValue)
);

// update a preset sequence
export const updatePresetSequence = createAsyncThunk(
  'equipment/updatePresetSequence',
  async (data, { rejectWithValue }) =>
    apiCall(() => axiosInstance.patch(`preset/sequence/`, data), rejectWithValue)
);

// delete a preset sequence
export const deletePresetSequence = createAsyncThunk(
  'equipment/deletePresetSequence',
  async (presetSequenceId, { rejectWithValue }) =>
    apiCall(() => axiosInstance.delete(`preset/sequence/${presetSequenceId}/`), rejectWithValue)
);

// apply a preset sequence
export const applyPresetSequence = createAsyncThunk(
  'equipment/applyPresetSequence',
  async (data, { rejectWithValue }) =>
    apiCall(() => axiosInstance.post(`preset/sequence/apply/`, data), rejectWithValue)
);

// stop a preset sequence
export const stopPresetSequence = createAsyncThunk(
  'equipment/stopPresetSequence',
  async (data, { rejectWithValue }) =>
    apiCall(() => axiosInstance.post(`preset/sequence/stop/`, data), rejectWithValue)
);

// Create or update preset sequence image
export const updatePresetSequenceImage = createAsyncThunk(
  'equipment/updatePresetSequenceImage',
  async (
    { presetSequenceId, imgType = 'Large', lastUpdated = '', defaultImage = '' },
    { rejectWithValue }
  ) => {
    try {
      const result = await uploadImageAndRefresh({
        id: presetSequenceId,
        sourceId: null,
        imgType,
        endpointPrefix: 'preset/sequence',
        sourceEndpointPrefix: null,
        lastUpdated,
        defaultImage,
      });
      return result;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.detail?.[0]?.msg || error?.message || 'Failed to update block image'
      );
    }
  }
);
// ---------------------------------------------------
// Preset Sequence Step
// ---------------------------------------------------

// get all preset sequence steps for a preset sequence by id
export const fetchPresetSequenceStepsForSequence = createAsyncThunk(
  'equipment/fetchPresetSequenceStepsForSequence',
  async (presetSequenceId, { rejectWithValue }) =>
    apiCall(() => axiosInstance.get(`preset/sequence/steps/${presetSequenceId}/`), rejectWithValue)
);

// get a preset sequence step by id
export const fetchPresetSequenceStepById = createAsyncThunk(
  'equipment/fetchPresetSequenceStepById',
  async (presetSequenceStepId, { rejectWithValue }) =>
    apiCall(
      () => axiosInstance.get(`preset/sequence/step/${presetSequenceStepId}/`),
      rejectWithValue
    )
);

// create a preset sequence step
export const createPresetSequenceStep = createAsyncThunk(
  'equipment/createPresetSequenceStep',
  async (data, { rejectWithValue }) =>
    apiCall(() => axiosInstance.post(`preset/sequence/step/`, data), rejectWithValue)
);

// update a preset sequence step
export const updatePresetSequenceStep = createAsyncThunk(
  'equipment/updatePresetSequenceStep',
  async (data, { rejectWithValue }) =>
    apiCall(() => axiosInstance.patch(`preset/sequence/step/`, data), rejectWithValue)
);

// Delete Preset sequence step
export const deletePresetSequenceStep = createAsyncThunk(
  'equipment/deletePresetSequenceStep',
  async (presetSequenceStepId, { rejectWithValue }) =>
    apiCall(
      () => axiosInstance.delete(`preset/sequence/step/${presetSequenceStepId}/`),
      rejectWithValue
    )
);

// Park Morail devices in equipment group
export const parkEquipmentGroup = createAsyncThunk(
  'equipment/parkEquipmentGroup',
  async (equipGroupUUID, { getState, rejectWithValue, dispatch }) =>
    apiCall(
      () => axiosInstance.post('equipment-group/park/', { equip_group_uuid: equipGroupUUID }),
      rejectWithValue
    )
);
